import { useContext, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import ValidatedSelect from "../../components/ValidatedSelect/ValidatedSelect";
import ButtonFotter from "../../components/Button/ButtonFotter/ButtonFotter";
import ValidatedInput from "../../components/ValidatedInput/ValidatedInput";
import Typography from "../../components/Typography/Typography";
import Loader from "../../components/Loader/Loader";
import Button from "../../components/Button/Button";
import styles from "./ValidateIdentity.module.scss";
import Switch from "../../components/Switch/Switch";
import Modal from "../../components/Modal/Modal";
import codes from "../../utils/codes";

import { days, monthNumbers, years } from "../../utils/utils";
import { StateContext } from "../../contexts/state.context";
import { cedulaValidate } from "../../utils/validators";
import { formatStrNumber } from "../../utils/functions";
import { submitFormData } from "../../utils/services";

const RECAPTCHA_KEY = window.env.REACT_APP_GOOGLE_KEY;
const TREATMENT_POLICY = window.env.REACT_APP_TREATMENT_POLICY;
const TERMS_AND_CONDITIONS = window.env.REACT_APP_TERMS_AND_CONDITIONS;
const NUM_TRIES = window.env.REACT_APP_NUM_TRIES;

const ValidateIdentity = () => {
	const navigate = useNavigate();
	const outletContext = useOutletContext();
	const setShellFooter = outletContext?.setShellFooter;
	const { setFormData } = useContext(StateContext);

	const [policy, setPolicy] = useState();
	const [termsCondition, setTermsCondition] = useState();

	const [docType, setDocType] = useState();
	const [docTypeError, setDocTypeError] = useState(false);

	const [id, setId] = useState();
	const [idError, setIdError] = useState();

	const [day, setDay] = useState();
	const [year, setYear] = useState();
	const [month, setMonth] = useState();
	const [dateError, setDateError] = useState(false);

	const [loader, setLoader] = useState(false);
	const [wrongDataModal, setWrongDataModal] = useState(false);
	const [invalidDocumentModal, setInvalidDocumentModal] = useState(false);
	const [redirectModal, setRedirectModal] = useState(false);

	const [token, setToken] = useState(null);
	const [captchaError, setCaptchaError] = useState(false);

	const [numTries, setNumTries] = useState(NUM_TRIES);

	const handleOpenPolicies = () => {
		window.open(TREATMENT_POLICY, "_blank", "noopener,noreferrer");
	};

	const handleOpenTerms = () => {
		window.open(TERMS_AND_CONDITIONS, "_blank", "noopener,noreferrer");
	};

	const validateExpDate = () => {
		if (
			!month ||
			!year ||
			!day ||
			month === "Mes" ||
			year === "Año" ||
			day === "Día"
		)
			return true;
		return false;
	};

	const getDocType = () => {
		switch (docType) {
			case "Cédula de ciudadanía":
				return "CC";
			case "Cédula de extranjería":
				return "CE";
			case "Pasaporte":
				return "PT";
			default:
				return "CC";
		}
	};

	const handleNumTries = () => {
		setNumTries((n) => n - 1);
		setWrongDataModal(false);
	};

	const handleReturnClick = () => {
		navigate("/login");
	};

	const handleDisabledBtn = () => {
		if (
			policy &&
			termsCondition &&
			docType &&
			id &&
			day &&
			month &&
			year &&
			token
		)
			return false;
		else return true;
	};

	const handleContinueClick = () => {
		if (!docType || docType === "Selecciona tipo de documento") {
			setDocTypeError(true);
			return;
		}

		if (!id || cedulaValidate(id)) {
			setIdError(true);
			return;
		}

		if (validateExpDate()) {
			setDateError(true);
			return;
		}

		if (!token) {
			console.error("NO TOKEN");
			setCaptchaError(true);
			return;
		}

		setLoader(true);

		const rData = {};
		rData.id = id.replace(/[^\d]/g, "");
		rData.docType = getDocType();
		// Identica expects ISO 8601 calendar date (YYYY-MM-DD), not DD/MM/YYYY.
		rData.fechaExpedicionDocumento = `${year}-${month}-${String(day).padStart(2, "0")}`;

		submitFormData({
			formInfo: rData,
		})
			.then(({ code, message }) => {
				switch (code) {
					case codes.JVB001.code: {
						const ani = message?.aniSource;
						if (ani?.apellidos) {
							rData.apellidos = ani.apellidos;
						}
						if (ani?.nombres) {
							rData.nombres = ani.nombres;
						}
						setFormData({ ...rData, captchaToken: token });
						navigate("/register/personal-data");
						break;
					}
					case codes.JVB004.code:
						setLoader(false);
						setRedirectModal(true);
						break;

					default:
						setLoader(false);
						setWrongDataModal(true);
						break;
				}
			})
			.catch((e) => {
				console.log(e.data);
				setLoader(false);
			});
	};

	useEffect(() => {
		if (!setShellFooter) return;
		setShellFooter(
			<ButtonFotter
				dock="panel"
				onClick={handleContinueClick}
				disabled={handleDisabledBtn()}
			>
				Continuar
			</ButtonFotter>,
		);
		return () => setShellFooter(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when inputs change; handlers use latest closure
	}, [
		setShellFooter,
		policy,
		termsCondition,
		docType,
		id,
		day,
		month,
		year,
		token,
	]);

	useEffect(() => {
		setFormData(null);
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (docType === "Cédula de extranjería" || docType === "Pasaporte")
			setInvalidDocumentModal(true);
	}, [docType]);

	return (
		<div className={styles.validateIdentityContainer}>
			<div className={styles.validateIdentityPolicyContainer}>
				<Switch
					className={styles.validateIdentityPolicy}
					onChange={setPolicy}
				></Switch>
				<Typography>
					He leído y autorizo de manera previa, expresa e informada el
					tratamiento de mis datos personales conforme a la{" "}
					<span
						className={styles.validateIdentityPolicyBlueTxt}
						onClick={handleOpenPolicies}
					>
						Política de Tratamiento de Datos Personales
					</span>
					.
				</Typography>
			</div>

			<div className={styles.validateIdentityPolicyContainer}>
				<Switch
					className={styles.validateIdentityPolicy}
					onChange={setTermsCondition}
				></Switch>
				<Typography>
					He leído y acepto los{" "}
					<span
						className={styles.validateIdentityPolicyBlueTxt}
						onClick={handleOpenTerms}
					>
						Términos y Condiciones
					</span>{" "}
					del programa Amigos Juan Valdez®.
				</Typography>
			</div>

			<ValidatedSelect
				className={styles.validateIdentitySelectContainer}
				label="Tipo de documento de identidad"
				placeholder="Selecciona tipo de documento"
				options={[
					"Cédula de ciudadanía",
					"Cédula de extranjería",
					"Pasaporte",
				]}
				onChange={setDocType}
				error={docTypeError}
				errorMessage="Campo inválido"
				setError={setDocTypeError}
			></ValidatedSelect>

			{docType && (
				<>
					<ValidatedInput
						className={styles.validateIdentityTextInputContainer}
						label="Número de documento de identidad"
						placeholder="X.XXX.XXX.XXX"
						type="text"
						inputMode="numeric"
						onChange={setId}
						error={idError}
						setError={setIdError}
						formatter={formatStrNumber}
					></ValidatedInput>

					<Typography
						className={styles.validateIdentityTextInputContainer}
						variant="h6"
						bold
					>
						Fecha de expedición del documento
					</Typography>

					<div className={styles.validateIdentityExpDateContainer}>
						<ValidatedSelect
							placeholder="Día"
							options={days}
							onChange={setDay}
							error={dateError}
							setError={setDateError}
							center
						></ValidatedSelect>
						<ValidatedSelect
							placeholder="Mes"
							options={monthNumbers}
							onChange={setMonth}
							error={dateError}
							setError={setDateError}
							center
						></ValidatedSelect>
						<ValidatedSelect
							placeholder="Año"
							options={years}
							onChange={setYear}
							error={dateError}
							setError={setDateError}
							center
						></ValidatedSelect>
					</div>

					<Typography
						className={styles.validateIdentityExpDateError}
						variant="p2"
						color={dateError ? "red" : "transparent"}
					>
						Fecha inválida
					</Typography>

					<div className={styles.validateIdentityReCaptchaContainer}>
						<div className={styles.validateIdentityReCaptcha}>
							<ReCAPTCHA
								sitekey={RECAPTCHA_KEY}
								hl="es"
								onChange={(token) => {
									setCaptchaError(false);
									setToken(token);
								}}
							></ReCAPTCHA>

							{captchaError && (
								<div
									className={
										styles.validateIdentityReCaptchaBorder
									}
								>
									<Typography
										className={
											styles.validateIdentityCaptchaError
										}
										variant="p2"
										color="red"
									>
										Debe realizar el reCAPTCHA
									</Typography>
								</div>
							)}
						</div>
					</div>
				</>
			)}

			{wrongDataModal && (
				<Modal
					title="Los datos no coinciden."
					onClose={() => setWrongDataModal(false)}
				>
					<div>
						<Typography className={styles.modalText}>
							La información que ingresaste no coincide con un
							documento de identidad válido. Por favor, revisa los
							datos e inténtalo de nuevo.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={handleNumTries}
						>
							Intentar de nuevo
						</Button>
					</div>
				</Modal>
			)}

			{invalidDocumentModal && (
				<Modal title="¡Ups! Documento no válido.">
					<div>
						<Typography className={styles.modalText}>
							Por el momento, solo puedes inscribirte con cédula
							de ciudadanía colombiana.<br></br>
							Si tienes otro tipo de documento, pronto estaremos
							habilitando esta opción.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={() => navigate("/")}
						>
							Volver
						</Button>
					</div>
				</Modal>
			)}

			{redirectModal && (
				<Modal title="Ya estás registrado como Amigo Juan Valdez">
					<div>
						<Typography className={styles.modalText}>
							Por favor inicia sesión con los datos registrados.
							Si no recuerdas la contraseña, puedes recuperarla en{" "}
							<span className={styles.modalTextSpan}>
								"Olvide mi contraseña"
							</span>{" "}
							Dirígete a Soy Amigo Juan Valdez.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={handleReturnClick}
						>
							Ir a Soy Amigo Juan Valdez
						</Button>
					</div>
				</Modal>
			)}

			{!numTries && (
				<Modal title="Protocolo de Seguridad Activado">
					<div>
						<Typography className={styles.modalText}>
							Tu solicitud de verificación no pudo ser procesada
							automáticamente y ha sido marcada para una revisión
							de seguridad. Para cualquier aclaración, contacta al
							área de soporte al correo <br></br>
							<span
								className={styles.validateIdentityPolicyBlueTxt}
							>
								cuentaconjuan@juanvaldezcafe.com
							</span>
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={() => navigate("/")}
						>
							Volver a Inicio
						</Button>
					</div>
				</Modal>
			)}

			{loader && <Loader></Loader>}
		</div>
	);
};

export default ValidateIdentity;
